import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth';
import {
  MessagePackageCatalogItem,
  MessagePackagesService,
} from '../../../services/message-packages.service';
import { EpaycoLoaderService, PageTitleService } from '../../../shared/services';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-settings-messages',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe],
  templateUrl: './messages.html',
  styleUrl: './messages.css',
})
export class Messages implements OnInit {
  private readonly titleService = inject(PageTitleService);
  private readonly service = inject(MessagePackagesService);
  private readonly epaycoLoader = inject(EpaycoLoaderService);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly buying = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly catalog = signal<MessagePackageCatalogItem[]>([]);
  readonly selectedCatalogId = signal<string | null>(null);
  readonly customQuantity = signal<number | null>(null);
  readonly status = signal<{
    freeQuotaLimit: number;
    freeUsed: number;
    freeRemaining: number;
    packageTotalCredits: number;
    packageUsedCredits: number;
    packageRemainingCredits: number;
    totalRemainingCredits: number;
    cycleStartUtc: string;
    cycleEndUtc: string;
  } | null>(null);

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Mensajes de WhatsApp',
      subtitle: 'Mensajes disponibles este mes y paquetes adicionales',
      breadcrumbs: [{ name: 'Configuración', url: '/settings' }],
    });

    this.handlePaymentCallback();
    this.loadData();
  }

  async loadData() {
    this.loading.set(true);
    this.error.set(null);
    try {
      const [catalog, status] = await Promise.all([
        firstValueFrom(this.service.getCatalog()),
        firstValueFrom(this.service.getStatus()),
      ]);
      this.catalog.set(catalog);
      this.status.set(status);

      if (!this.selectedCatalogId() && catalog.length > 0) {
        this.selectedCatalogId.set(catalog[0].messagePackageCatalogId);
      }
    } catch (err: unknown) {
      this.error.set(this.resolveLoadError(err));
    } finally {
      this.loading.set(false);
    }
  }

  selectPackage(item: MessagePackageCatalogItem) {
    this.selectedCatalogId.set(item.messagePackageCatalogId);
  }

  getSelectedPackage(): MessagePackageCatalogItem | undefined {
    const selectedId = this.selectedCatalogId();
    return this.catalog().find((item) => item.messagePackageCatalogId === selectedId);
  }

  isSelectedPackageCustom(): boolean {
    return !!this.getSelectedPackage()?.isCustom;
  }

  onCustomQuantityInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.setCustomQuantity(target?.value ?? '');
  }

  setCustomQuantity(value: string) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      this.customQuantity.set(parsed);
      return;
    }
    this.customQuantity.set(null);
  }

  async buySelectedPackage() {
    this.buying.set(true);
    this.error.set(null);
    this.success.set(null);
    try {
      const selectedCatalogId = this.selectedCatalogId();
      const selectedPackage = this.catalog().find(
        (x) => x.messagePackageCatalogId === selectedCatalogId,
      );

      if (!selectedPackage) {
        throw new Error('Selecciona un paquete para continuar.');
      }

      const customQuantity = selectedPackage.isCustom ? this.customQuantity() : null;
      if (selectedPackage.isCustom && (!customQuantity || customQuantity <= 5000)) {
        throw new Error('Para paquete personalizado debes indicar más de 5000 mensajes.');
      }

      const checkout = await firstValueFrom(
        this.service.createCheckout({
          messagePackageCatalogId: selectedPackage.messagePackageCatalogId,
          customQuantity,
        }),
      );

      const userInfo = this.authService.getUserInfo() as any;
      const fullName = `${userInfo?.given_name ?? ''} ${userInfo?.family_name ?? ''}`.trim();
      const ePayco = await this.epaycoLoader.load();
      const handler = ePayco.checkout.configure({
        key: environment.epaycoPublicKey,
        test: environment.epaycoTestMode,
      });

      const responseUrl = `${window.location.origin}/settings/messages`;
      const webhookBase = new URL(environment.epaycoWebhookUrl).origin;
      const confirmationUrl = `${webhookBase}/api/v1/messagepackages/epayco/webhook`;

      handler.open({
        name: 'CitaLink Pro - Mensajes WhatsApp',
        description: checkout.description,
        invoice: checkout.invoiceReference,
        amount: checkout.amountCop,
        currency: checkout.currency,
        country: 'CO',
        response: responseUrl,
        confirmation: confirmationUrl,
        test: environment.epaycoTestMode,
        extra1: checkout.extra1BusinessId,
        extra2: checkout.extra2PurchaseId,
        extra3: userInfo?.email ?? '',
        payerEmail: userInfo?.email ?? '',
        payerFirstName: userInfo?.given_name ?? '',
        payerLastName: userInfo?.family_name ?? '',
        payerPhone: userInfo?.phone ?? '',
        name_billing: fullName,
        email_billing: userInfo?.email ?? '',
        mobilephone_billing: userInfo?.phone ?? '',
      });
    } catch (err: any) {
      this.error.set(err?.message ?? 'No se pudo iniciar el proceso de pago.');
    } finally {
      this.buying.set(false);
    }
  }

  private handlePaymentCallback() {
    const params = this.route.snapshot.queryParamMap;
    const status = params.get('x_response');
    if (!status) {
      return;
    }

    const normalized = status.toLowerCase();
    if (['aceptada', 'approved', 'paid', 'success'].includes(normalized)) {
      this.success.set('Pago recibido. Estamos acreditando tus mensajes.');
      setTimeout(() => this.loadData(), 2000);
    } else {
      this.error.set(
        params.get('x_response_reason_text') ?? 'El pago no fue aprobado.',
      );
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private resolveLoadError(err: unknown): string {
    if (!(err instanceof HttpErrorResponse)) {
      return 'No fue posible cargar la información de mensajes.';
    }

    if (err.status === 401) {
      return 'Tu sesión expiró. Inicia sesión de nuevo.';
    }

    if (err.status === 403) {
      return 'No tienes permisos para consultar los mensajes de WhatsApp.';
    }

    const backendMessage = err.error?.message ?? err.error?.errors?.[0];
    if (typeof backendMessage === 'string' && backendMessage.trim().length > 0) {
      return backendMessage;
    }

    return `No fue posible cargar la información de mensajes (HTTP ${err.status || 0}).`;
  }
}
